import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import * as TokenStorage from '@edulastic/api/src/utils/Storage'
import PropTypes from 'prop-types'
import {
  CustomModalStyled,
  EduButton,
  FlexContainer,
  SpinLoader,
} from '@edulastic/common'
import { roleuser } from '@edulastic/constants'
import { Button, Divider } from 'antd'
import { themeColor } from '@edulastic/colors'
import styled from 'styled-components'
import { getUserRole } from '../author/src/selectors/user'
import {
  getTokenLoading,
  getUserId,
  refreshTokenAction,
  getUserFirstName,
  getLogoutInProgress,
} from '../student/Login/ducks'
import TimeDisplay from './TimeDisplay'
import { logoutAction } from '../author/src/actions/auth'
import { CustomSSORedirectUrl } from '../author/src/constants/customSSOConst'

const JWT_WARNING_TIME =
  (process.env.REACT_APP_JWT_WARNING_TIME || 15) * 60 * 1000 // show JWT expiry warning at 15 minutes before expiry (15 minutes)
const EXPIRY_CHECK_INTERVAL = 5 * 1000 // Check token expiry every 5 seconds

const SessionTimer = ({
  userId,
  userRole,
  refreshToken,
  tokenLoading,
  logout,
  userName,
  logoutInProgress,
}) => {
  const [showWarning, setShowWarning] = useState(false)
  const [displayTime, setDisplayTime] = useState(0)
  const warningAcknowledgedRef = useRef(false)
  const checkIntervalRef = useRef()
  const countdownIntervalRef = useRef()
  const showWarningRef = useRef(false)

  const updateShowWarning = (value) => {
    showWarningRef.current = value
    setShowWarning(value)
  }

  // Function to check JWT expiration
  const checkJWTExpiration = () => {
    const token = TokenStorage.getAccessToken()
    if (!token) return null

    try {
      const tokenData = TokenStorage.parseJwt(token)
      const expirationTime = tokenData.exp * 1000 // Convert to milliseconds
      const timeUntilExpiry = expirationTime - Date.now()
      setDisplayTime(timeUntilExpiry)
      return timeUntilExpiry
    } catch (error) {
      console.error('Error checking JWT expiration:', error)
      return null
    }
  }

  const checkAndUpdateTokenStatus = () => {
    const timeUntilExpiry = checkJWTExpiration()

    if (timeUntilExpiry === null) return

    // If token has expired
    if (timeUntilExpiry <= 0) {
      updateShowWarning(false)
      // Clear all timers
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
      // redirect NHED user to NHED portal
      // example key - appid:guid:65422cd97d4cf964ba7f196b
      // Example value - {"appid":"NHED_TEST","guid":"<roster_id>"}
      const isAppidGuidPresent = localStorage.getItem(`appid:guid:${userId}`)
      // Build the query parameters dynamically
      const buildQueryParams = (params) => {
        return new URLSearchParams(params).toString()
      }
      let customRedirectUrl = ''
      if (isAppidGuidPresent) {
        const appidGuidObj = JSON.parse(isAppidGuidPresent)
        // Create the href with parameters
        customRedirectUrl = `${
          CustomSSORedirectUrl[appidGuidObj.appid]?.url
        }?${buildQueryParams({
          ...CustomSSORedirectUrl[appidGuidObj.appid]?.params,
          UserID: appidGuidObj.guid,
        })}`
        window.location.href = customRedirectUrl
      } else {
        // Store session expired state before redirecting
        sessionStorage.setItem('sessionExpired', 'true')
        logout()
      }
      return
    }
    // Show warning when time until expiry is less than warning time
    // BUT only if not already acknowledged by student
    if (
      timeUntilExpiry <= JWT_WARNING_TIME &&
      !showWarningRef.current &&
      !warningAcknowledgedRef.current
    ) {
      updateShowWarning(true)
    }
    // If the user has multiple tabs open and one tab refreshes the token,
    // then hide the warning in the other tabs as well (since the token is already refreshed).
    if (timeUntilExpiry > JWT_WARNING_TIME && showWarningRef.current) {
      updateShowWarning(false)
    }
  }

  const setupExpiryTimer = () => {
    try {
      // Clear existing timers
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }

      // Check JWT expiration initially
      checkJWTExpiration()

      // Set up interval to check token status
      checkIntervalRef.current = setInterval(
        checkAndUpdateTokenStatus,
        EXPIRY_CHECK_INTERVAL
      )
    } catch (error) {
      console.error('Error in setupExpiryTimer:', error)
    }
  }

  // Effect for countdown timer
  useEffect(() => {
    if (showWarning) {
      // Clear any existing countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }

      // Start a new countdown interval
      countdownIntervalRef.current = setInterval(() => {
        setDisplayTime((prev) => {
          const newTime = prev - 1000
          return newTime > 0 ? newTime : 0
        })
      }, 1000)
    } else if (countdownIntervalRef.current) {
      // Clear countdown interval when warning is hidden
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [showWarning])

  useEffect(() => {
    if (!tokenLoading) {
      updateShowWarning(false)
      setupExpiryTimer()
    }
  }, [tokenLoading])

  useEffect(() => {
    if (!userId) {
      console.log('No user ID, skipping timer setup')
      return
    }

    // Initial timer setup
    setupExpiryTimer()
    // Cleanup function
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [userId])

  const handleTokenRefresh = () => {
    refreshToken()
  }

  const closeWarningModal = () => {
    updateShowWarning(false)
    // For students, when they click OK, mark as acknowledged
    if (userRole === roleuser.STUDENT) {
      warningAcknowledgedRef.current = true
    }
  }

  const logoutUser = () => {
    updateShowWarning(false)
    logout()
  }

  // convert displayTime to h:mm:ss
  const displayTimeString = new Date(displayTime).toISOString().substr(11, 8)

  return (
    <>
      <CustomModalStyled
        padding="0px"
        bodyPadding="0px 25px"
        modalWidth="fit-content"
        modalMaxWidth="fit-content"
        title={
          <>
            <ModalTitle>Session Ending Soon!</ModalTitle>
            <Divider />
          </>
        }
        closable={false}
        maskClosable={false}
        centered
        visible={showWarning}
        footer={[
          <CustomFlexContainer
            gap="10px"
            justifyContent="center"
            padding="25px 20px"
            width="100%"
            alignItems="center"
          >
            <EduButton
              borderRadius="1px"
              width={userRole === roleuser.STUDENT ? '120px' : '180px'}
              loading={tokenLoading}
              disabled={logoutInProgress}
              onClick={
                userRole === roleuser.STUDENT
                  ? closeWarningModal
                  : handleTokenRefresh
              }
            >
              {userRole === roleuser.STUDENT ? 'ok' : 'Continue Session'}
            </EduButton>
            <StyledBtn
              type="link"
              disabled={tokenLoading || logoutInProgress}
              width="120px"
              onClick={logoutUser}
            >
              Sign Out
            </StyledBtn>
          </CustomFlexContainer>,
        ]}
      >
        <div>
          <FlexContainer
            marginBottom="12px"
            alignItems="flex-start"
            justifyContent="start"
            gap="14px"
          >
            <StyledText>
              Hi <BoldText>{userName}</BoldText>, your session will end in{' '}
            </StyledText>
            <TimeDisplay
              minutes={displayTimeString.split(':')[1]}
              seconds={displayTimeString.split(':')[2]}
            />
            .{' '}
          </FlexContainer>
          <p>
            {userRole === roleuser.STUDENT
              ? 'Please save your work, if needed.'
              : 'Select Continue Session to stay signed in.'}
          </p>
        </div>
      </CustomModalStyled>
      {logoutInProgress && <SpinLoader spinning={logoutInProgress} />}
    </>
  )
}

SessionTimer.propTypes = {
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  userRole: PropTypes.string.isRequired,
  refreshToken: PropTypes.func.isRequired,
  tokenLoading: PropTypes.bool.isRequired,
}

const enhance = compose(
  connect(
    (state) => ({
      userId: getUserId(state),
      userName: getUserFirstName(state),
      userRole: getUserRole(state),
      tokenLoading: getTokenLoading(state),
      logoutInProgress: getLogoutInProgress(state),
    }),
    {
      refreshToken: refreshTokenAction,
      logout: logoutAction,
    }
  )
)

export default enhance(SessionTimer)

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 400;
  padding: 25px 20px 0px;
  margin: 0;
`

const StyledText = styled.p`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
`

const BoldText = styled.span`
  font-size: 14px;
  font-weight: 800;
  width: fit-content;
  white-space: nowrap;
  word-break: keep-all;
`

export const StyledBtn = styled(Button)`
  color: ${themeColor};
  border: none;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  :hover,
  :active {
    color: ${themeColor};
  }
`
const CustomFlexContainer = styled(FlexContainer)`
  flex-direction: row-reverse;
`
